import { useEffect, useRef } from 'react';

const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
const DEFAULT_RECONNECT_STORM_LIMIT = 3;
const DEFAULT_RECONNECT_STORM_WINDOW_MS = 30000;
const VALID_RESOURCES = new Set(['sessions', 'projects', 'all']);
const ROUTE_UNAVAILABLE_STATUSES = new Set([404, 405, 501]);
const PROJECTION_INVALIDATION_HANDLERS = new Map([
  ['session_projection_updated', (event, notify) => (
    ['sessions', 'all'].includes(event.resource) && notify?.(event)
  )],
]);

function shouldRefreshForListEvent(event) {
  const eventType = String(event?.event_type || '').trim();
  const resource = String(event?.resource || '').trim();
  return eventType === 'list_invalidated' && VALID_RESOURCES.has(resource);
}

function resolveReconnectDelay(attempt) {
  const index = Math.max(0, Math.min(Number(attempt) || 0, DEFAULT_RECONNECT_DELAYS_MS.length - 1));
  return DEFAULT_RECONNECT_DELAYS_MS[index];
}

function resolveTimestamp(now) {
  const value = Number(typeof now === 'function' ? now() : Date.now());
  return Number.isFinite(value) ? value : Date.now();
}

function createReconnectStormTracker({
  now = Date.now,
  windowMs = DEFAULT_RECONNECT_STORM_WINDOW_MS,
  limit = DEFAULT_RECONNECT_STORM_LIMIT,
} = {}) {
  const resolvedWindowMs = Math.max(0, Number(windowMs) || DEFAULT_RECONNECT_STORM_WINDOW_MS);
  const resolvedLimit = Math.max(1, Number(limit) || DEFAULT_RECONNECT_STORM_LIMIT);
  let disconnectTimes = [];

  const recordDisconnect = () => {
    const timestamp = resolveTimestamp(now);
    disconnectTimes = [
      ...disconnectTimes.filter((item) => timestamp - item <= resolvedWindowMs),
      timestamp,
    ];
    return disconnectTimes.length >= resolvedLimit;
  };

  return { recordDisconnect };
}

/**
 * Detect a permanently unavailable list-events transport route.
 *
 * The list-events subscription is an optimization over canonical list polling.
 * When a local or older backend does not yet expose the SSE endpoint, retrying
 * forever creates console noise and disables the heartbeat fallback.  Treating
 * deployment-level route failures as "unavailable" lets the app stop this SSE
 * attempt and resume polling, while transient network/server errors still use
 * the normal reconnect backoff path.
 */
function isListEventRouteUnavailableError(error) {
  const status = Number(error?.status || 0);
  return ROUTE_UNAVAILABLE_STATUSES.has(status);
}

/**
 * Subscribe to backend list invalidation events and refresh canonical lists.
 *
 * @param {object} params - Hook params.
 * @param {boolean} params.enabled - Whether the subscription is active.
 * @param {object} params.listEventsAPI - Adapter exposing subscribe().
 * @param {Function} params.onRefresh - Canonical list refresh callback.
 * @param {Function} params.onProjectionInvalidated - Existing Session read owner; no argument on reconnect recovery.
 * @param {Function} params.onUnavailable - Callback invoked when the backend
 *   route is not deployed.  This lets the app fall back to heartbeat polling
 *   instead of repeatedly reconnecting to a permanent 404/405/501.
 * @param {number} params.debounceMs - Refresh debounce window.
 * @returns {void}
 */
export default function useSessionListEvents({
  enabled = true,
  listEventsAPI,
  onRefresh,
  onProjectionInvalidated,
  onUnavailable,
  debounceMs = DEFAULT_DEBOUNCE_MS,
} = {}) {
  const onRefreshRef = useRef(onRefresh);
  const onProjectionInvalidatedRef = useRef(onProjectionInvalidated);
  const onUnavailableRef = useRef(onUnavailable);
  const debounceTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const connectTimerRef = useRef(null);
  onRefreshRef.current = onRefresh;
  onProjectionInvalidatedRef.current = onProjectionInvalidated;
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    if (
      !enabled
      || typeof listEventsAPI?.subscribe !== 'function'
      || typeof onRefreshRef.current !== 'function'
      || typeof window === 'undefined'
      || typeof AbortController === 'undefined'
    ) {
      return undefined;
    }

    let stopped = false;
    let reconnectAttempt = 0;
    let activeController = null;
    const reconnectStormTracker = createReconnectStormTracker();
    const isCurrentConnection = (controller) => (
      !stopped && activeController === controller && !controller.signal.aborted
    );

    const clearDebounce = () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    const clearRetry = () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
    const clearConnectTimer = () => {
      if (connectTimerRef.current) {
        window.clearTimeout(connectTimerRef.current);
        connectTimerRef.current = null;
      }
    };
    const scheduleRefresh = (controller) => {
      clearDebounce();
      const resolvedDebounceMs = Math.max(0, Number(debounceMs) || DEFAULT_DEBOUNCE_MS);
      debounceTimerRef.current = window.setTimeout(() => {
        if (!isCurrentConnection(controller)) return;
        Promise.resolve(onRefreshRef.current?.()).catch(() => {});
      }, resolvedDebounceMs);
    };
    const scheduleReconnect = (connect) => {
      clearRetry();
      const delay = resolveReconnectDelay(reconnectAttempt);
      reconnectAttempt += 1;
      retryTimerRef.current = window.setTimeout(connect, delay);
    };
    const shouldReconnect = (error) => {
      if (isListEventRouteUnavailableError(error)) {
        onUnavailableRef.current?.(error);
        return false;
      }
      if (reconnectStormTracker.recordDisconnect()) {
        onUnavailableRef.current?.(error);
        return false;
      }
      return true;
    };
    const connect = () => {
      if (stopped) {
        return;
      }
      const controller = new AbortController();
      activeController = controller;
      Promise.resolve(listEventsAPI.subscribe({
        signal: controller.signal,
        onEvent: (event) => {
          if (!isCurrentConnection(controller)) return;
          reconnectAttempt = 0;
          if (shouldRefreshForListEvent(event)) {
            scheduleRefresh(controller);
            Promise.resolve(PROJECTION_INVALIDATION_HANDLERS.get(event?.reason)?.(
              event, onProjectionInvalidatedRef.current,
            )).catch(() => {});
          }
        },
      })).then(() => {
        if (isCurrentConnection(controller) && shouldReconnect()) {
          scheduleReconnect(reconnect);
        }
      }).catch((error) => {
        if (isCurrentConnection(controller)) {
          if (shouldReconnect(error)) {
            scheduleReconnect(reconnect);
          }
        }
      });
    };
    const reconnect = () => {
      if (stopped) return;
      // Recover missed invalidations only after a disconnected subscription.
      Promise.resolve(onRefreshRef.current?.()).catch(() => {});
      Promise.resolve(onProjectionInvalidatedRef.current?.()).catch(() => {});
      connect();
    };

    connectTimerRef.current = window.setTimeout(connect, 0);

    return () => {
      stopped = true;
      clearDebounce();
      clearRetry();
      clearConnectTimer();
      activeController?.abort();
    };
  }, [debounceMs, enabled, listEventsAPI]);
}

export {
  createReconnectStormTracker,
  isListEventRouteUnavailableError,
  shouldRefreshForListEvent,
  useSessionListEvents,
};
